import type { WizardStepName } from "../api/types.js";

export interface UiWizardStep {
  id: WizardStepName;
  label: string;
}

/**
 * Only user-visible steps — excludes auto-advance steps that the user
 * never interacts with (account_setup, add_website).
 */
export const UI_WIZARD_STEPS: UiWizardStep[] = [
  { id: "plan_selection", label: "Choose plan" },
  { id: "scan_depth", label: "Scan depth" },
  { id: "scan_frequency", label: "Scan frequency" },
  { id: "wcag_standard", label: "WCAG standard" },
  { id: "discovery_preview", label: "Intake checks" },
  { id: "protected_site", label: "Verification" },
  { id: "statement_setup", label: "Statement & standard" },
  { id: "review_launch", label: "Review" },
];

const VISIBLE_ORDER: WizardStepName[] = UI_WIZARD_STEPS.map((s) => s.id);

interface WizardShellProps {
  currentStep: WizardStepName;
  completedSteps: WizardStepName[];
  children: React.ReactNode;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function WizardShell({
  currentStep,
  completedSteps,
  children,
  onBack,
  canGoBack = true,
}: WizardShellProps) {
  const currentIdx = VISIBLE_ORDER.indexOf(currentStep);
  const totalSteps = VISIBLE_ORDER.length;
  // Clamp to 1 for auto-advance steps that aren't in the visible list
  const stepNumber = currentIdx >= 0 ? currentIdx + 1 : 1;

  return (
    <div className="wizard-shell">
      <aside>
        <div className="wizard-progress">
          <p className="wizard-progress__text">
            Step {stepNumber} of {totalSteps}
          </p>
          <div className="wizard-progress__bar">
            <div
              className="wizard-progress__fill"
              style={{ width: `${(stepNumber / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <nav className="wizard-steps" aria-label="Wizard steps">
          {UI_WIZARD_STEPS.map((step) => {
            const isCompleted = completedSteps.includes(step.id);
            const isActive = step.id === currentStep;
            const stepIdx = VISIBLE_ORDER.indexOf(step.id);
            const isFuture = stepIdx > currentIdx && !isCompleted;

            let className = "wizard-steps__item";
            if (isActive) className += " wizard-steps__item--active";
            else if (isCompleted) className += " wizard-steps__item--completed";

            return (
              <div
                key={step.id}
                className={className}
                aria-current={isActive ? "step" : undefined}
              >
                {isCompleted ? (
                  <span className="wizard-steps__check" aria-hidden="true">
                    ✓
                  </span>
                ) : (
                  <span className="wizard-steps__dot" aria-hidden="true" />
                )}
                <span style={{ opacity: isFuture ? 0.5 : 1 }}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </nav>

        {onBack && canGoBack && (
          <button
            onClick={onBack}
            className="wizard-back-btn"
            aria-label="Go to previous step"
          >
            ← Back
          </button>
        )}
      </aside>
      <div>{children}</div>
    </div>
  );
}
